import { useEffect, useState, useCallback } from 'react'
import leadService, { LEAD_STATUS_GROUPS, LEAD_STATUS_LABELS } from '../../services/leadService'
import teamService from '../../services/teamService'
import { HiOutlineDownload, HiOutlineChartBar, HiOutlineUserGroup, HiOutlineLocationMarker } from 'react-icons/hi'

const RANGES = [['', 'Tất cả'], ['today', 'Hôm nay'], ['yesterday', 'Hôm qua'], ['7d', '7 ngày'], ['14d', '14 ngày'], ['30d', '30 ngày'], ['month', 'Tháng này']]
const REGIONS = ['Đà Nẵng', 'HUẾ', 'QUY NHƠN', 'GIÁM ĐỐC NHÂN SỰ']
const SOURCES = ['Facebook', 'TikTok', 'Zalo', 'Form thủ công']

function Tile({ label, value, sub, color = 'text-primary-600' }) {
  return (
    <div className="card p-4">
      <div className="text-2xs uppercase tracking-wide text-secondary-400">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      {sub && <div className="text-2xs text-secondary-400 mt-0.5">{sub}</div>}
    </div>
  )
}

export default function AdminReport() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [range, setRange] = useState('30d')
  const [month, setMonth] = useState('')
  const [handler, setHandler] = useState('')
  const [region, setRegion] = useState('')
  const [source, setSource] = useState('')
  const [handlers, setHandlers] = useState([])

  useEffect(() => {
    teamService.assignableUsers().then((r) => { if (r.status === 'success') setHandlers(r.data) }).catch(() => {})
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (month) params.month = month
      else if (range) params.range = range
      if (handler) params.assigned_to = handler
      if (region) params.region = region
      if (source) params.source = source
      const res = await leadService.report(params)
      if (res.status === 'success') setData(res.data)
    } finally { setLoading(false) }
  }, [range, month, handler, region, source])

  useEffect(() => { load() }, [load])

  const exportCsv = () => {
    if (!data) return
    const lines = ['Chỉ tiêu,Giá trị']
    lines.push(`Tổng leads,${data.total}`, `Trung bình/ngày,${data.avg_per_day}`, `Số ngày,${data.days}`, '')
    lines.push('PHỄU CHUYỂN ĐỔI,Số,Tỷ lệ %')
    data.funnel.forEach((f) => lines.push(`${f.label},${f.total},${f.pct}`))
    lines.push('', 'THEO NHÂN VIÊN,Số')
    data.by_handler.forEach((h) => lines.push(`${h.name},${h.total}`))
    lines.push('', 'THEO KHU VỰC,Số')
    data.by_region.forEach((r) => lines.push(`${r.region},${r.total}`))
    lines.push('', 'THEO TRẠNG THÁI,Số')
    Object.entries(data.by_status).forEach(([s, c]) => lines.push(`${(LEAD_STATUS_LABELS[s] || s).replace(/,/g, ' ')},${c}`))
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `bao-cao-leads-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 30000)
  }

  const maxFunnel = data?.funnel?.[0]?.total || 1
  const maxHandler = Math.max(1, ...(data?.by_handler || []).map((h) => h.total))

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Báo cáo tổng hợp</h1>
          <p className="page-subtitle">Thống kê lead theo thời gian, nhân viên, khu vực, phễu chuyển đổi</p>
        </div>
        <button onClick={exportCsv} disabled={!data} className="btn-secondary"><HiOutlineDownload className="w-4 h-4" /> Xuất CSV</button>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map(([v, l]) => (
            <button key={v || 'all'} type="button" onClick={() => { setRange(v); setMonth('') }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${!month && range === v ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-secondary-600 border-secondary-200 hover:border-primary-400'}`}>
              {l}
            </button>
          ))}
          <input type="month" className="input w-auto text-xs" value={month} onChange={(e) => setMonth(e.target.value)} title="Tháng cụ thể" />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select className="select sm:w-48" value={handler} onChange={(e) => setHandler(e.target.value)}>
            <option value="">Tất cả nhân viên</option>
            <option value="unassigned">Chưa giao</option>
            {handlers.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <select className="select sm:w-44" value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="">Tất cả khu vực</option>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="select sm:w-40" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Tất cả nguồn</option>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Tile label="Tổng leads" value={data.total} />
            <Tile label="Trung bình / ngày" value={data.avg_per_day} color="text-emerald-600" />
            <Tile label="Số ngày có lead" value={data.days} color="text-secondary-700" />
            <Tile label="Đã cập nhật trạng thái" value={`${data.reliability.updated}/${data.total}`}
              sub={`Chưa xử lý: ${data.reliability.pending}`} color="text-amber-600" />
          </div>

          {/* Phễu chuyển đổi */}
          <div className="card p-4 mb-4">
            <div className="flex items-center gap-1.5 mb-3 font-medium text-secondary-800"><HiOutlineChartBar className="w-4 h-4" /> Phễu chuyển đổi (8 bước)</div>
            <div className="space-y-2">
              {data.funnel.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-40 text-sm text-secondary-600 shrink-0">{f.label}</div>
                  <div className="flex-1 bg-secondary-100 rounded-full h-6 overflow-hidden">
                    <div className="h-full bg-primary-500 rounded-full flex items-center justify-end pr-2 text-2xs text-white font-medium"
                      style={{ width: `${Math.max(4, (f.total / maxFunnel) * 100)}%` }}>{f.total}</div>
                  </div>
                  <div className="w-12 text-right text-2xs text-secondary-400">{f.pct}%</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-4">
            {/* Theo nhân viên */}
            <div className="card p-4">
              <div className="flex items-center gap-1.5 mb-3 font-medium text-secondary-800"><HiOutlineUserGroup className="w-4 h-4" /> Theo nhân viên</div>
              <div className="space-y-1.5">
                {data.by_handler.map((h) => (
                  <div key={h.user_id ?? 'none'} className="flex items-center gap-2 text-sm">
                    <div className="w-28 truncate text-secondary-700">{h.name}</div>
                    <div className="flex-1 bg-secondary-100 rounded h-4 overflow-hidden">
                      <div className="h-full bg-violet-400" style={{ width: `${(h.total / maxHandler) * 100}%` }} />
                    </div>
                    <div className="w-10 text-right font-medium text-secondary-700">{h.total}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Theo khu vực */}
            <div className="card p-4">
              <div className="flex items-center gap-1.5 mb-3 font-medium text-secondary-800"><HiOutlineLocationMarker className="w-4 h-4" /> Theo khu vực</div>
              <table className="w-full text-sm">
                <tbody>
                  {data.by_region.map((r) => (
                    <tr key={r.region} className="border-b border-secondary-50 last:border-0">
                      <td className="py-2 text-secondary-700">{r.region}</td>
                      <td className="py-2 text-right font-medium">{r.total}</td>
                      <td className="py-2 text-right text-2xs text-secondary-400 w-14">{data.total ? Math.round(r.total * 100 / data.total) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Theo trạng thái (4 nhóm) */}
          <div className="card p-4">
            <div className="font-medium text-secondary-800 mb-3">Chi tiết theo trạng thái (18)</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {LEAD_STATUS_GROUPS.map((g) => (
                <div key={g.key}>
                  <div className={`text-xs font-semibold mb-1.5 ${g.badge}`}>{g.label}</div>
                  <div className="space-y-1">
                    {g.statuses.map((s) => (
                      <div key={s.value} className="flex justify-between text-sm">
                        <span className="text-secondary-600 truncate mr-2">{s.label}</span>
                        <span className="font-medium text-secondary-800">{data.by_status[s.value] || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
